import { oneOf } from "@postdaily/api/lib/one-of";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { SIGN_UP_LINK } from "@/lib/auth-links";
import { PLATFORM_CATALOG, platformPopularityRank } from "@/lib/platforms";
import { BrandMark } from "./brand-mark";
import { ProductScreenshot } from "./product/product-screenshot";

const trust = [
  "Free forever plan",
  "No credit card to start",
  "Cancel anytime",
];

const liveChannels = PLATFORM_CATALOG.filter(
  (channel) => channel.status === "live",
);

/**
 * The hero screenshot is a product preview: the demo workspace in it connects
 * the networks people ask for, and three of those adapters are still waiting on
 * platform approval. The line under the image names exactly those three so the
 * gap between the picture and today's reality is stated rather than implied.
 *
 * Keep this list in step with the channels the capture actually shows — see
 * `scripts/marketing-screenshots/seed-demo.sql`. Labels and the remaining count
 * come from the catalog, so they cannot drift as adapters ship: once a network
 * here flips to `live` it drops out of this line on its own.
 */
const HERO_PREVIEW_CHANNELS = ["instagram", "x", "tiktok"] as const;
const isHeroPreviewChannel = oneOf(HERO_PREVIEW_CHANNELS);

const soonChannels = PLATFORM_CATALOG.filter(
  (channel) => channel.status === "soon",
);

const previewPending = soonChannels
  .filter((channel) => isHeroPreviewChannel(channel.id))
  .sort((a, b) => platformPopularityRank(a.id) - platformPopularityRank(b.id))
  .map((channel) => channel.label);

export function LandingHero() {
  return (
    <section className="relative overflow-hidden">
      {/* decorative background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-[180px] left-1/2 z-0 h-[640px] w-[1100px] -translate-x-1/2 blur-2xl"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 40%,oklch(0.72 0.16 268 / 0.3),transparent 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-10 -right-[120px] z-0 size-[520px] blur-3xl"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%,oklch(0.7 0.15 290 / 0.22),transparent 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage:
            "linear-gradient(oklch(0.5 0.05 264 / 0.05) 1px,transparent 1px),linear-gradient(90deg,oklch(0.5 0.05 264 / 0.05) 1px,transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(90% 60% at 50% 20%,#000 0%,transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(90% 60% at 50% 20%,#000 0%,transparent 75%)",
        }}
      />

      <div className="relative z-[1] mx-auto flex max-w-[1200px] flex-col items-center px-6 pt-[92px] pb-[52px] text-center">
        <h1 className="max-w-[15ch] font-heading font-normal text-[clamp(46px,6.8vw,88px)] text-foreground leading-[1.02] tracking-[-0.02em]">
          Post every day, without the{" "}
          <span
            // The tight leading + italic face let Instrument Serif's glyph
            // overhangs (the final "d", the "y"/"g" descenders) fall outside the
            // bg-clip-text paint box and clip. box-decoration-break:clone plus a
            // little padding grows that box per line fragment; -mr cancels the
            // right padding's layout shift so the trailing period stays tight.
            className="-mr-[0.14em] bg-clip-text pt-[0.05em] pr-[0.14em] pb-[0.16em] text-transparent italic [-webkit-box-decoration-break:clone] [box-decoration-break:clone]"
            style={{
              backgroundImage:
                "linear-gradient(120deg,oklch(0.55 0.17 264),oklch(0.56 0.18 288))",
            }}
          >
            daily grind
          </span>
          .
        </h1>

        <p className="mt-[22px] max-w-[600px] text-[clamp(17px,1.4vw,19.5px)] text-[oklch(0.46_0.02_264)] leading-relaxed">
          PostDaily is a social media scheduler that plans your content, fills
          your posting queue and publishes it for you — with an AI composer, an
          automatic queue and a publishing engine that never drops a post.
        </p>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            {...SIGN_UP_LINK}
            className="pd-brand-grad inline-flex h-[52px] items-center gap-2 rounded-[13px] px-6 font-semibold text-base text-white no-underline transition-transform hover:-translate-y-0.5"
          >
            Start for free
            <ArrowRight className="size-[17px]" strokeWidth={2.2} />
          </Link>
          <a
            href="#product"
            className="inline-flex h-[52px] items-center gap-2.5 rounded-[13px] border border-border bg-card px-[22px] font-semibold text-[oklch(0.26_0.02_264)] text-base no-underline shadow-[0_2px_8px_-4px_oklch(0.4_0.08_264/0.14)] transition-all hover:-translate-y-0.5 hover:border-[oklch(0.82_0.03_264)]"
          >
            <span className="inline-flex size-[26px] items-center justify-center rounded-full bg-[oklch(0.95_0.03_264)] text-primary">
              <ChevronDown className="size-[15px]" strokeWidth={2.6} />
            </span>
            Look inside first
          </a>
        </div>

        <div className="mt-5 inline-flex flex-wrap items-center justify-center gap-3.5 text-[13.5px] text-muted-foreground">
          {trust.map((item) => (
            <span key={item} className="inline-flex items-center gap-1.5">
              <Check
                className="size-[15px] text-[oklch(0.6_0.13_155)]"
                strokeWidth={2.4}
              />
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* the product itself, front and centre */}
      <div className="relative z-[1] mx-auto max-w-[1080px] px-6 pb-[76px]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-10 top-8 bottom-24 blur-3xl"
          style={{
            background:
              "radial-gradient(60% 50% at 50% 50%,oklch(0.62 0.16 268 / 0.22),transparent 75%)",
          }}
        />
        <div className="relative rounded-[18px] border border-[oklch(0.92_0.02_264)] bg-[oklch(1_0_0/0.7)] p-1.5 backdrop-blur-sm sm:p-2.5">
          <ProductScreenshot
            alt="The PostDaily composer: one post written once for several networks at a time, with a live preview of each network beside the editor."
            priority
            // max-w-[1080px] container with px-6, inside a p-1.5/sm:p-2.5 frame.
            sizes="(min-width: 1128px) 1012px, calc(100vw - 60px)"
            slug="composer"
          />
        </div>

        <p className="relative mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[13.5px] text-muted-foreground">
          <span>Connect today:</span>
          {liveChannels.map((channel) => (
            <span
              key={channel.id}
              className="inline-flex items-center gap-1.5 font-medium text-[oklch(0.35_0.02_264)]"
            >
              <BrandMark
                platform={channel.id}
                className="size-[18px] rounded-[5px]"
                iconClassName="size-[10px]"
              />
              {channel.label}
            </span>
          ))}
        </p>
        {previewPending.length > 0 && (
          <p className="relative mx-auto mt-2 max-w-[620px] text-center text-[13px] text-muted-foreground">
            {previewPending.slice(0, -1).join(", ")}
            {previewPending.length > 1 ? " and " : ""}
            {previewPending.at(-1)} are shown above as a preview — they go live
            as each platform approves us.{" "}
            <Link
              to="/integrations"
              className="font-semibold text-primary no-underline"
            >
              See where every network stands
            </Link>
            .
          </p>
        )}
      </div>
    </section>
  );
}
